import React, { useEffect, useState, useCallback } from 'react';
import {
  Layers,
  Download,
  Sparkles,
  ArrowRight,
  Loader,
  AlertCircle,
} from 'lucide-react';
import { Masterplan, getProjectMasterplans, processPrompt } from '../../services/api';
import { downloadMasterplanHTML } from '../../utils/masterplanHtmlExport';
import GlassCard from '../ui/GlassCard';
import GlassButton from '../ui/GlassButton';
import './MasterplanPanel.css';

interface StreamKeywords {
  core_local?: string[];
  jurisdiction_specific?: string[];
  niche?: string[];
  long_tail?: string[];
  local_boosters?: string[];
}

interface StreamCompetitorSource {
  name: string;
  url: string;
  relevance: string;
}

interface StreamArticle {
  title: string;
  summary: string;
  prompt?: string;
  target_keywords?: string[];
  word_count?: string;
}

interface StreamData {
  stream_name: string;
  keywords?: StreamKeywords;
  keyword_rationale?: string;
  competitor_sources?: StreamCompetitorSource[];
  competitor_rationale?: string;
  articles?: StreamArticle[];
}

const KEYWORD_GROUP_LABELS: Record<keyof StreamKeywords, string> = {
  core_local: 'Core / Local',
  jurisdiction_specific: 'Jurisdiction-specific',
  niche: 'Niche',
  long_tail: 'Long-tail',
  local_boosters: 'Local boosters',
};

interface MasterplanPanelProps {
  projectId: string | null;
  onGenerated?: () => void;
}

const selectMasterplan = (masterplans: Masterplan[]): Masterplan | null => {
  if (!masterplans || masterplans.length === 0) return null;
  const sorted = [...masterplans].sort((a, b) => {
    const aCompleted = a.generation_status === 'completed' ? 1 : 0;
    const bCompleted = b.generation_status === 'completed' ? 1 : 0;
    if (aCompleted !== bCompleted) return bCompleted - aCompleted;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
  return sorted[0];
};

const MasterplanPanel: React.FC<MasterplanPanelProps> = ({ projectId, onGenerated }) => {
  const [masterplan, setMasterplan] = useState<Masterplan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatingStream, setGeneratingStream] = useState<string | null>(null);

  const loadMasterplan = useCallback(async () => {
    if (!projectId) {
      setMasterplan(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const masterplans = await getProjectMasterplans(projectId);
      setMasterplan(selectMasterplan(masterplans));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load masterplan.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadMasterplan();
  }, [loadMasterplan]);

  const handleGenerateArticles = async (streamName: string) => {
    if (!projectId || generatingStream) return;
    setGeneratingStream(streamName);
    try {
      await processPrompt(projectId, `generate articles for ${streamName}`);
      onGenerated?.();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to start article generation.');
    } finally {
      setGeneratingStream(null);
    }
  };

  const handleDownloadReport = () => {
    if (!masterplan) return;
    downloadMasterplanHTML(masterplan);
  };

  if (!projectId) {
    return (
      <div className="masterplan-panel">
        <GlassCard className="masterplan-empty-state">
          <Layers size={32} />
          <p>Select a project to view its masterplan.</p>
        </GlassCard>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="masterplan-panel">
        <div className="masterplan-loading">
          <Loader size={20} className="masterplan-spin" />
          <span>Loading masterplan&hellip;</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="masterplan-panel">
        <GlassCard className="masterplan-empty-state">
          <AlertCircle size={32} />
          <p>{error}</p>
        </GlassCard>
      </div>
    );
  }

  const streams: StreamData[] = (masterplan?.streams as StreamData[] | null) || [];

  if (!masterplan || streams.length === 0) {
    return (
      <div className="masterplan-panel">
        <GlassCard className="masterplan-empty-state">
          <Layers size={32} />
          <p>No masterplan yet. Generate one from the Content Creator&rsquo;s Masterplan wizard.</p>
          <GlassButton
            variant="primary"
            icon={<ArrowRight size={16} />}
            iconPosition="right"
            onClick={() => { window.location.href = '/content'; }}
          >
            Go to Content Creator
          </GlassButton>
        </GlassCard>
      </div>
    );
  }

  const createdDate = masterplan.created_at
    ? new Date(masterplan.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <div className="masterplan-panel">
      <div className="masterplan-header">
        <div className="masterplan-header-info">
          <h3><Layers size={16} /> Masterplan</h3>
          <div className="masterplan-header-meta">
            {masterplan.industry && <span>{masterplan.industry}</span>}
            {masterplan.business_type && <span>{masterplan.business_type}</span>}
            {masterplan.primary_location && <span>{masterplan.primary_location}</span>}
            <span>{streams.length} stream{streams.length === 1 ? '' : 's'}</span>
            {createdDate && <span>Created {createdDate}</span>}
          </div>
        </div>
        <GlassButton
          variant="secondary"
          icon={<Download size={16} />}
          onClick={handleDownloadReport}
        >
          Download Report
        </GlassButton>
      </div>

      {masterplan.generation_status !== 'completed' && (
        <div className="masterplan-status-banner">
          <AlertCircle size={16} />
          <span>This masterplan is still being generated ({masterplan.generation_status}). Streams shown below may be incomplete.</span>
        </div>
      )}

      <div className="masterplan-streams">
        {streams.map((stream, idx) => (
          <GlassCard key={`${stream.stream_name}-${idx}`} className="masterplan-stream-card">
            <div className="masterplan-stream-header">
              <h4><Layers size={16} /> {stream.stream_name}</h4>
              <GlassButton
                variant="primary"
                icon={<Sparkles size={16} />}
                disabled={!!generatingStream}
                loading={generatingStream === stream.stream_name}
                onClick={() => handleGenerateArticles(stream.stream_name)}
              >
                Generate Articles
              </GlassButton>
            </div>

            {stream.keywords && (
              <div className="masterplan-keyword-groups">
                {(Object.keys(KEYWORD_GROUP_LABELS) as Array<keyof StreamKeywords>).map((groupKey) => {
                  const keywords = stream.keywords?.[groupKey];
                  if (!keywords || keywords.length === 0) return null;
                  return (
                    <div className="masterplan-keyword-group" key={groupKey}>
                      <span className="masterplan-keyword-group-label">{KEYWORD_GROUP_LABELS[groupKey]}</span>
                      <div className="masterplan-keyword-tags">
                        {keywords.map((kw, kwIdx) => (
                          <span className="masterplan-keyword-tag" key={kwIdx}>{kw}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {stream.keyword_rationale && (
              <div className="masterplan-rationale">
                <span className="masterplan-rationale-label">Keyword rationale</span>
                <p>{stream.keyword_rationale}</p>
              </div>
            )}

            {stream.competitor_sources && stream.competitor_sources.length > 0 && (
              <div className="masterplan-competitors">
                <span className="masterplan-rationale-label">Competitor &amp; authority sources</span>
                {stream.competitor_sources.map((source, sIdx) => (
                  <div className="masterplan-competitor-item" key={sIdx}>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">{source.name}</a>
                    {source.relevance && <span className="masterplan-competitor-relevance">{source.relevance}</span>}
                  </div>
                ))}
                {stream.competitor_rationale && (
                  <p className="masterplan-competitor-rationale">{stream.competitor_rationale}</p>
                )}
              </div>
            )}

            {stream.articles && stream.articles.length > 0 && (
              <div className="masterplan-roadmap">
                <span className="masterplan-rationale-label">10-article roadmap</span>
                {stream.articles.map((article, aIdx) => (
                  <div className="masterplan-roadmap-row" key={aIdx}>
                    <span className="masterplan-roadmap-index">{aIdx + 1}</span>
                    <div className="masterplan-roadmap-content">
                      <span className="masterplan-roadmap-title">{article.title}</span>
                      {article.summary && <p className="masterplan-roadmap-summary">{article.summary}</p>}
                      {article.target_keywords && article.target_keywords.length > 0 && (
                        <div className="masterplan-keyword-tags">
                          {article.target_keywords.map((kw, kwIdx) => (
                            <span className="masterplan-keyword-tag" key={kwIdx}>{kw}</span>
                          ))}
                        </div>
                      )}
                      {article.word_count && (
                        <span className="masterplan-roadmap-wordcount">{article.word_count} words</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        ))}
      </div>
    </div>
  );
};

export default MasterplanPanel;
